import React, { useId } from 'react';
import { Text } from '../../atoms/Text';
import './FormField.css';

// Props type for form input elements that FormField can wrap
interface InputChildProps {
  id?: string;
  state?: 'default' | 'error' | 'success';
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
  'aria-required'?: boolean;
}

export interface FormFieldProps {
  /** The input element to wrap */
  children: React.ReactElement<InputChildProps>;
  /** Label text */
  label?: React.ReactNode;
  /** Label position */
  labelPosition?: 'top' | 'left' | 'hidden';
  /** Shows required asterisk */
  required?: boolean;
  /** Helper text shown below input */
  helperText?: React.ReactNode;
  /** Error message - overrides helperText and sets error state */
  error?: React.ReactNode;
  /** Success message - shows when no error */
  success?: React.ReactNode;
  /** Custom ID - auto-generated if not provided */
  id?: string;
  /** Full width */
  fullWidth?: boolean;
  /** Additional CSS class */
  className?: string;
}

export const FormField: React.FC<FormFieldProps> = ({
  children,
  label,
  labelPosition = 'top',
  required = false,
  helperText,
  error,
  success,
  id: customId,
  fullWidth = false,
  className = '',
}) => {
  const generatedId = useId();
  const fieldId = customId || generatedId;
  const helperId = `${fieldId}-helper`;

  // Determine which message to show
  const showError = Boolean(error);
  const showSuccess = Boolean(success) && !showError;
  const showHelper = Boolean(helperText) && !showError && !showSuccess;

  // Clone child and inject accessibility props
  const enhancedChild = React.cloneElement(children, {
    id: children.props.id || fieldId,
    'aria-describedby': error || success || helperText ? helperId : undefined,
    'aria-invalid': showError ? true : undefined,
    'aria-required': required ? true : undefined,
    // Set state prop if child supports it
    ...(showError && children.props.state === undefined && { state: 'error' }),
    ...(showSuccess && children.props.state === undefined && { state: 'success' }),
  });

  const classes = [
    'hi-ui-form-field',
    `hi-ui-form-field-label-${labelPosition}`,
    fullWidth && 'hi-ui-form-field-full-width',
    showError && 'hi-ui-form-field-has-error',
    showSuccess && 'hi-ui-form-field-has-success',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={classes}>
      {label && labelPosition !== 'hidden' && (
        <label htmlFor={children.props.id || fieldId} className="hi-ui-form-field-label">
          <Text as="span" variant="label" bold>
            {label}
            {required && (
              <span className="hi-ui-form-field-required" aria-hidden="true">
                {' '}
                *
              </span>
            )}
          </Text>
        </label>
      )}
      {label && labelPosition === 'hidden' && (
        <label htmlFor={children.props.id || fieldId} className="hi-ui-form-field-label-sr-only">
          {label}
          {required && ' (required)'}
        </label>
      )}
      <div className="hi-ui-form-field-control">{enhancedChild}</div>
      {(showError || showSuccess || showHelper) && (
        <div id={helperId} className="hi-ui-form-field-message">
          {showError && (
            <Text variant="error" className="hi-ui-form-field-error">
              {error}
            </Text>
          )}
          {showSuccess && (
            <Text variant="success" className="hi-ui-form-field-success">
              {success}
            </Text>
          )}
          {showHelper && (
            <Text variant="muted" className="hi-ui-form-field-helper">
              {helperText}
            </Text>
          )}
        </div>
      )}
    </div>
  );
};
